'use client';

import React, { useMemo, useCallback } from 'react';
import { PermissionsTree, TreeNode } from '@/components/platform/permissions';
import {
  APPLICATION_PERMISSIONS,
  rolesToPermissions,
  permissionsToRoles,
} from '@/lib/platform/permissions';
import { useEntitlements } from '@/lib/platform/hooks/use-entitlements';
import { isSponsorshipsEnabled } from '@/lib/platform/config/feature-flags';

// UPDATED: 2025-12-11 - Added app_settings permission
export type RoleType =
  | 'admin'
  | 'dashboard'
  | 'analytics'
  | 'reports'
  | 'methods'
  | 'valuations_sales'
  | 'valuations_transfer'
  | 'valuations_marketing'
  | 'valuations_brand'
  | 'approver'
  | 'sponsors'
  | 'packages'
  | 'inventory'
  | 'proposals'
  | 'users'
  | 'organisation'
  | 'app_settings'
  | 'billing'
  | 'plugins'
  | 'api'
  | 'sso'
  | 'audit_log'
  | 'documents';

interface PermissionsTreeSelectorProps {
  selectedRoles: RoleType[];
  onRolesChange: (roles: RoleType[]) => void;
  isAdmin?: boolean;
  canEditAdminRole?: boolean;
  mode?: 'edit' | 'invite';
  userRoles?: RoleType[]; // Current user's roles (for invite mode validation)
}

type PlanFeatures = ReturnType<typeof useEntitlements>['features'];

// Module-scope pure recursive helpers (React Compiler immutability rule
// disallows self-referencing useCallback functions)

/**
 * Filter permissions tree based on plan limits
 */
function filterPermissionsByPlan(nodes: TreeNode[], features: PlanFeatures): TreeNode[] {
  return nodes
    .map((node) => {
      const newNode = { ...node };

      // Plan-based restrictions
      // Hide Sponsorships section if feature flag disabled OR not Professional/Enterprise
      if (
        node.id === 'sponsorships' &&
        (!isSponsorshipsEnabled() || !features.sponsorshipSponsors)
      ) {
        return null;
      }

      // Hide Sponsorships > Packages if feature flag disabled OR not Enterprise
      if (
        node.id === 'sponsorships.packages' &&
        (!isSponsorshipsEnabled() || !features.sponsorshipPackages)
      ) {
        return null;
      }

      // Hide Sponsorships > Library if feature flag disabled OR not Enterprise
      if (
        node.id === 'sponsorships.library' &&
        (!isSponsorshipsEnabled() || !features.sponsorshipPackages)
      ) {
        return null;
      }

      // Hide Sponsorships > Proposals if feature flag disabled OR not Enterprise
      if (
        node.id === 'sponsorships.proposals' &&
        (!isSponsorshipsEnabled() || !features.sponsorshipPackages)
      ) {
        return null;
      }

      // Hide Sponsorships > Analytics if feature flag disabled OR not Professional/Enterprise
      if (
        node.id === 'sponsorships.analytics' &&
        (!isSponsorshipsEnabled() || !features.hasAdvancedReports)
      ) {
        return null;
      }

      // Hide Sponsorships > Sponsors if feature flag disabled
      if (node.id === 'sponsorships.sponsors' && !isSponsorshipsEnabled()) {
        return null;
      }

      // Hide Edit Valuations sub-permissions if not Enterprise
      if (node.id === 'valuations.valuations' && !features.hasGranularPermissions) {
        return null;
      }

      // Hide Reports if plan doesn't have it
      if (node.id === 'valuations.reports' && !features.hasAdvancedReports) {
        return null;
      }

      // Hide API if plan doesn't have it (Enterprise only)
      if (node.id === 'platform.api' && !features.restApi) {
        return null;
      }

      // Hide Audit Log if plan doesn't have it (Enterprise only)
      if (node.id === 'platform.settings.advanced' && !features.auditLog) {
        return null;
      }

      // Recursively filter children
      if (newNode.children) {
        const filteredChildren = filterPermissionsByPlan(newNode.children, features);
        if (filteredChildren.length === 0) {
          // If all children are hidden, hide parent too
          return null;
        }
        newNode.children = filteredChildren;
      }

      return newNode;
    })
    .filter((node): node is TreeNode => node !== null);
}

/**
 * Filter permissions tree based on user's own permissions (invite mode only)
 * Users can only grant permissions they have themselves
 */
function filterPermissionsByUserPermissions(
  nodes: TreeNode[],
  userPermissions: Set<string>
): TreeNode[] {
  return nodes
    .map((node) => {
      const newNode = { ...node };

      // Check if user has this permission
      if (!userPermissions.has(node.id)) {
        return null;
      }

      // Recursively filter children
      if (newNode.children) {
        const filteredChildren = filterPermissionsByUserPermissions(
          newNode.children,
          userPermissions
        );
        if (filteredChildren.length === 0 && node.id !== 'admin') {
          // If all children are hidden and it's not admin, hide parent too
          return null;
        }
        newNode.children = filteredChildren;
      }

      return newNode;
    })
    .filter((node): node is TreeNode => node !== null);
}

export default function PermissionsTreeSelector({
  selectedRoles,
  onRolesChange,
  isAdmin = false,
  canEditAdminRole = false,
  mode = 'edit',
  userRoles = [],
}: PermissionsTreeSelectorProps) {
  const { features } = useEntitlements();

  /**
   * Apply all filters to permissions tree
   */
  const filteredPermissions = useMemo(() => {
    let permissions = APPLICATION_PERMISSIONS;

    // Filter by plan limits
    permissions = filterPermissionsByPlan(permissions, features);

    // Filter by user's own roles (invite mode only)
    // In edit mode or if user is admin, show all permissions
    if (mode !== 'edit' && !isAdmin) {
      const userPermissions = new Set(rolesToPermissions(userRoles.join(',')));
      permissions = filterPermissionsByUserPermissions(permissions, userPermissions);
    }

    // Hide admin permission if user can't edit admin role
    if (!canEditAdminRole) {
      permissions = permissions.filter((node) => node.id !== 'admin');
    }

    return permissions;
  }, [features, mode, isAdmin, userRoles, canEditAdminRole]);

  /**
   * Convert selected roles to permission IDs for tree component
   */
  const selectedPermissions = useMemo(() => {
    return rolesToPermissions(selectedRoles.join(','));
  }, [selectedRoles]);

  /**
   * Handle permission selection change from tree
   * Convert permission IDs back to roles and update state
   */
  const handlePermissionChange = useCallback(
    (permissionIds: string[]) => {
      // Convert permission IDs to roles
      const newRoles = permissionsToRoles(permissionIds).split(',').filter(Boolean) as RoleType[];
      onRolesChange(newRoles);
    },
    [onRolesChange]
  );

  return (
    <PermissionsTree
      data={filteredPermissions}
      value={selectedPermissions}
      onChange={handlePermissionChange}
    />
  );
}
